import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './answer-button.module.css'

/**
 * Distinct from `AnswerButtonState` in `ear-training/` — renamed to avoid the
 * barrel-export collision in `music-ui/src/index.ts` once both packages
 * re-export their answer button. Same shape as ear-training's.
 */
export type RhythmAnswerButtonState = 'idle' | 'correct' | 'incorrect' | 'dimmed'

export interface RhythmAnswerButtonProps {
  /** Stable identifier passed back via `onPick`. */
  pickValue: string
  /** Lexicon id whose `displayName` + level-resolved `tag` populate the button. */
  lexiconId: string
  /** Fallback long-name when the lexicon has no entry (or `theoryLevel === 'off'`). */
  fallbackLongName: string
  /** Fallback short-name. */
  fallbackShortName: string
  state: RhythmAnswerButtonState
  onPick: (pickValue: string) => void
}

/**
 * Rhythm Trainer answer-button primitive used by both Meter ID and Subdivision
 * ID question panels. Mirrors `EarTrainingAnswerButton` 1:1 — state drives
 * disabled-ness + visual treatment (idle interactive; correct / incorrect /
 * dimmed post-answer). The long name comes from the lexicon's `displayName`;
 * the short tag from the level-resolved `tag`.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §6.2
 */
export const RhythmAnswerButton: FC<RhythmAnswerButtonProps> = props => {
  const { pickValue, lexiconId, fallbackLongName, fallbackShortName, state, onPick } = props
  const copy = useTheoryCopy(lexiconId)
  const longName = copy?.displayName ?? fallbackLongName
  const shortTag = copy?.tag ?? fallbackShortName
  const isInteractive = state === 'idle'
  const stateClass = styles[state] ?? ''
  return (
    <button
      type="button"
      className={`${styles.button} ${stateClass}`}
      disabled={!isInteractive}
      aria-pressed={state === 'correct'}
      onClick={() => onPick(pickValue)}
    >
      <span className={styles.longName}>{longName}</span>
      <span className={styles.shortTag}>{shortTag}</span>
    </button>
  )
}
